import React, {useCallback, useEffect, useState} from "react";
import {useEEToolAdapterContext} from "../contexts/eeToolAdapterContext";
import {useEEToolContext} from "../contexts/eeToolContext";
import DataGrid, {Column, Editing, Format, Lookup} from 'devextreme-react/data-grid';
import Toolbar, {Item as ToolbarItem} from "devextreme-react/toolbar";
import {Button} from "devextreme-react/button";
import {useEstimateProducts} from "../api/estimate-products";
import {OptionalItemsPopupControl} from "./OptionalItemsPopupControl";
import {EstimatedProduct} from "../types/EstimatedProduct";
import {LoadPagePanel, LoadPopupPanel} from "../../../../components";
import {LoadPanel} from "devextreme-react";
import {formatNumber} from "devextreme/localization";
import {MaterialOptionsPopupControl} from "./MaterialOptionsPopupControl";

export default function EstimateProductsControl(props: {
    showCompareMaterialOptions: boolean;
}) {
    const adapterContext = useEEToolAdapterContext();
    const context = useEEToolContext();
    const estimateProducts = useEstimateProducts(context.dealerId, context.itemizedCharges, context.optionalProducts, context.customerZip, context.retailPriceBucketID, false)
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isMaterialOptionsPopupVisible, setIsMaterialOptionsPopupVisible] = useState(false);
    const [total, setTotal] = useState(0);
    const [rTotal, setRTotal] = useState(0);
    const [rQty, setRQty] = useState(0);

    useEffect(() => {
        if (estimateProducts.data) {
            setTotal(estimateProducts.data.reduce((accumulator, currentValue) => {
                return accumulator + currentValue.extendedPrice;
            }, 0));
            if (context.materialProducts.length > 0) {
                setRTotal(estimateProducts.data.reduce((accumulator, currentValue) => {
                    if (currentValue.productID === context.materialProducts[0])
                        return accumulator;
                    return accumulator + currentValue.extendedPrice;
                }, 0));
                setRQty(estimateProducts.data.reduce((accumulator, currentValue) => {
                    if (currentValue.productID === context.materialProducts[0])
                        return accumulator + currentValue.qty;
                    return accumulator;
                }, 0));
            }
            
            // Capture retail price bucket ID from first product if available
            const bucketId = estimateProducts.data.find(p => p.retailPriceBucketId)?.retailPriceBucketId;
            if (bucketId && bucketId !== context.retailPriceBucketID) {
                context.setRetailPriceBucketID(bucketId);
            }
        }
    }, [estimateProducts.data]);

    useEffect(() => {
        if (!props.showCompareMaterialOptions) {
            setIsPopupVisible(true);
        }
    }, []);

    return <>
        <OptionalItemsPopupControl
            visible={isPopupVisible}
            onPopupHidden={() => {
                setIsPopupVisible(false)
            }}
            onProductsAdded={(e) => {
                context.addOptionalProducts(e);
            }}></OptionalItemsPopupControl>
        <MaterialOptionsPopupControl
            visible={isMaterialOptionsPopupVisible}
            total={rTotal}
            qty={rQty}
            onPopupHidden={() => {
                setIsMaterialOptionsPopupVisible(false)
            }}
        ></MaterialOptionsPopupControl>
        <Toolbar className='button-toolbar'>
            <ToolbarItem
                visible={true}
                location={'before'}
            >
                <Button
                    text="Add Optional Item(s)"
                    icon="Inscyth-Add"
                    elementAttr={{'data-testid': 'btn-add-optional-item'}}
                    onClick={() => {
                        setIsPopupVisible(true);
                    }}>
                </Button>
                <Button
                    text="Compare Material Options"
                    elementAttr={{'data-testid': 'btn-compare'}}
                    style={{marginLeft: "3px"}}
                    visible={props.showCompareMaterialOptions}
                    onClick={() => {
                        setIsMaterialOptionsPopupVisible(true);
                    }}>
                </Button>
            </ToolbarItem>
        </Toolbar>
        <LoadPanel
            visible={estimateProducts.isLoading}
            position={{of: '#optional-items-grid'}}
        />
        <div className='dx-card wide-card'>
            <div className='grid-header'>Estimate Details</div>
            <DataGrid
                id="optional-items-grid"
                className='grid-with-header'
                loadPanel={{enabled: true}}
                dataSource={estimateProducts.data}
                allowColumnResizing={true}
                showBorders={true}
                showColumnLines={true}
                columnAutoWidth={false}
                wordWrapEnabled={true}
                keyExpr={'quoteItemsID'}
                onRowRemoved={(e) => {
                    let product = e.data as EstimatedProduct;
                    context.deleteOptionalItem(product.quoteItemsID);
                }}
            >
                <Editing
                    mode="cell"
                    confirmDelete={true}
                    allowDeleting={(e) => {
                        return e.row.data.quoteItemsID < 0;
                    }}
                    allowUpdating={false}
                />
                <Column type={'buttons'} width={40}>
                </Column>
                <Column
                    dataField={'productLine'}
                    caption={'Product Line'}
                    dataType="string"
                    width={200}
                    allowEditing={false}
                />
                <Column
                    dataField={'productType'}
                    caption={'Product Type'}
                    dataType="string"
                    width={120}
                    allowEditing={false}
                />
                <Column
                    dataField={'thickness'}
                    caption={'Thickness'}
                    dataType="string"
                    width={80}
                    allowEditing={false}
                />
                <Column
                    dataField={'sku'}
                    caption={'SKU'}
                    dataType="string"
                    allowEditing={false}
                    width={100}
                />
                <Column
                    dataField={'description'}
                    caption={'Item Description'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'qty'}
                    caption={'Qty'}
                    dataType="number"
                    allowEditing={false}
                    width={80}
                />
                <Column
                    dataField={'uom'}
                    caption={'UOM'}
                    dataType="string"
                    width={100}
                    allowEditing={false}
                />
                <Column
                    dataField={'retailPrice'}
                    caption={'Retail Price'}
                    dataType="number"
                    allowEditing={false}
                    width={120}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
                <Column
                    dataField={'extendedPrice'}
                    caption={'Extended Price'}
                    dataType="number"
                    allowEditing={false}
                    width={120}
                >
                    <Format type="currency" precision={2}></Format>
                </Column>
            </DataGrid>
            <div className='dx-clearfix'>
                <table className='summary-table'>
                    <tbody>
                    <tr>
                        <td>Subtotal:</td>
                        <td>{formatNumber(total, {type: 'currency', precision: 2})}</td>
                    </tr>
                    <tr className='total'>
                        <td>Total:</td>
                        <td>{formatNumber(total, {type: 'currency', precision: 2})}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </>;
}